import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Panel, SettingRow, StatusChip, adminButton } from '@/components/admin/ui';
import { useToast } from '@/hooks/use-toast';
import {
  useStripeConnectStatus,
  useStripeDisconnect,
  useStripeOnboard,
  canTakeCardPayments,
  maskStripeAccountId,
} from '@/hooks/useStripeConnect';
import { cn } from '@/lib/utils';

interface StripeConnectPanelProps {
  orgId: string | null | undefined;
  /** Owner / platform admin: may connect, disconnect and change the switch. */
  canManage: boolean;
  checkoutEnabled: boolean;
  onCheckoutEnabledChange: (enabled: boolean) => void;
}

export function StripeConnectPanel({ orgId, canManage, checkoutEnabled, onCheckoutEnabledChange }: StripeConnectPanelProps) {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusQuery = useStripeConnectStatus(orgId);
  const onboard = useStripeOnboard(orgId);
  const disconnect = useStripeDisconnect(orgId);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const handledReturn = useRef(false);

  const status = statusQuery.data;

  const startOnboarding = async () => {
    try {
      const url = await onboard.mutateAsync();
      window.location.assign(url);
    } catch (err) {
      toast({ title: 'Could not start Stripe setup', description: (err as Error).message, variant: 'destructive' });
    }
  };

  // Stripe sends the owner back to /admin/payments?stripe=return|refresh.
  const stripeParam = searchParams.get('stripe');
  useEffect(() => {
    if (!stripeParam || !orgId || handledReturn.current) return;
    handledReturn.current = true;
    const clear = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('stripe');
      setSearchParams(next, { replace: true });
    };
    if (stripeParam === 'refresh') {
      // Onboarding link expired: mint a fresh one and send them straight back.
      clear();
      void startOnboarding();
      return;
    }
    if (stripeParam === 'return') {
      void statusQuery.refetch().then((res) => {
        if (canTakeCardPayments(res.data)) {
          toast({ title: 'Stripe connected', description: 'You can now take card payments online.' });
        } else {
          toast({ title: 'Stripe still needs a few details', description: 'Use "Continue setup" to finish.' });
        }
      });
    }
    clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeParam, orgId]);

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync();
      onCheckoutEnabledChange(false);
      toast({ title: 'Stripe disconnected', description: 'Online card payments have been turned off.' });
    } catch (err) {
      toast({ title: 'Could not disconnect Stripe', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setConfirmDisconnect(false);
    }
  };

  const chip = (() => {
    if (!status) return null;
    if (!status.configured) return <StatusChip tone="grey" dot={false}>Coming soon</StatusChip>;
    if (!status.connected) return <StatusChip tone="grey">Not connected</StatusChip>;
    if (!status.chargesEnabled) return <StatusChip tone="amber">Setup incomplete</StatusChip>;
    return <StatusChip tone="green">Connected</StatusChip>;
  })();

  const connectButton = (label: string, Icon = CreditCard) => (
    <Button
      size="sm"
      className={adminButton.primary}
      onClick={startOnboarding}
      disabled={!canManage || onboard.isPending}
    >
      {onboard.isPending ? <Loader2 className="animate-spin" /> : <Icon />}
      {label}
    </Button>
  );

  const missing: string[] = [];
  if (status?.connected) {
    if (!status.detailsSubmitted) missing.push('Business and identity details');
    if (!status.chargesEnabled) missing.push('Card payments are not enabled yet');
    if (!status.payoutsEnabled) missing.push('Payouts are not enabled yet (add a bank account)');
  }

  const switchAllowed = canTakeCardPayments(status);

  return (
    <Panel
      title={
        <span className="flex flex-wrap items-center gap-2">
          Card payments with Stripe
          {chip}
        </span>
      }
      icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
      description="Take card payments on invoices. Money goes straight to your own Stripe account."
      bodyClassName="pt-1 md:pt-1"
    >
      {statusQuery.isLoading && (
        <div className="flex items-center py-4 text-[13px] text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking Stripe…
        </div>
      )}

      {statusQuery.isError && (
        <div className="flex flex-wrap items-center justify-between gap-3 py-3.5">
          <p className="text-[12.5px] text-muted-foreground">
            We couldn't check your Stripe connection just now.
          </p>
          <Button size="sm" className={adminButton.outline} onClick={() => statusQuery.refetch()}>
            <RefreshCw /> Try again
          </Button>
        </div>
      )}

      {status && !status.configured && (
        <div className="flex flex-wrap items-center justify-between gap-3 py-3.5">
          <p className="min-w-0 flex-1 basis-[220px] text-[12.5px] text-muted-foreground">
            Online card payments are being switched on — check back soon.
          </p>
          <Button size="sm" className={adminButton.primary} disabled>
            <CreditCard /> Connect Stripe
          </Button>
        </div>
      )}

      {status?.configured && !status.connected && (
        <div className="space-y-3 py-3.5">
          <ul className="list-disc space-y-1 pl-5 text-[12.5px] leading-relaxed text-muted-foreground">
            <li>Payments go straight to your own Stripe account. We take 0% commission.</li>
            <li>Stripe's standard card fees apply.</li>
            <li>Apple Pay, Google Pay and PayPal can be switched on from your Stripe dashboard.</li>
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            {connectButton('Connect Stripe')}
            {!canManage && (
              <span className="text-[12px] text-muted-foreground">Only the organisation owner can connect Stripe.</span>
            )}
          </div>
        </div>
      )}

      {status?.connected && (
        <div className="adm-divide">
          {!status.chargesEnabled && (
            <div className="space-y-3 py-3.5" data-testid="stripe-incomplete">
              <div>
                <p className="text-[13.5px] font-semibold text-foreground">Finish setting up Stripe</p>
                <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                  Stripe needs a few more details before you can take card payments.
                </p>
              </div>
              <ul className="list-disc space-y-1 pl-5 text-[12.5px] text-muted-foreground">
                {missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-3">
                {connectButton('Finish setting up Stripe')}
                {!canManage && (
                  <span className="text-[12px] text-muted-foreground">Only the organisation owner can continue setup.</span>
                )}
              </div>
            </div>
          )}

          <SettingRow
            className="bg-card"
            title="Stripe account"
            description={
              <span className="font-mono text-[12px]" data-testid="stripe-account-id">
                {maskStripeAccountId(status.accountId)}
              </span>
            }
            control={
              <Button asChild size="sm" className={adminButton.outline}>
                <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer">
                  Open Stripe dashboard <ExternalLink />
                </a>
              </Button>
            }
          />

          <SettingRow
            className="bg-card"
            htmlFor="stripe-checkout-enabled"
            title="Take card payments online"
            description={
              switchAllowed
                ? 'Customers pay invoices by card through a secure Stripe Checkout page. Money goes to your Stripe account.'
                : 'Available once Stripe has finished verifying your account and card payments are enabled.'
            }
            control={
              <Switch
                id="stripe-checkout-enabled"
                checked={switchAllowed && checkoutEnabled}
                onCheckedChange={onCheckoutEnabledChange}
                disabled={!canManage || !switchAllowed}
              />
            }
          />

          {canManage && (
            <div className="flex justify-end pt-3">
              <Button
                size="sm"
                variant="ghost"
                className={cn(adminButton.ghost, 'text-[12.5px]')}
                onClick={() => setConfirmDisconnect(true)}
                disabled={disconnect.isPending}
              >
                Disconnect Stripe
              </Button>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Stripe?</AlertDialogTitle>
            <AlertDialogDescription>
              Customers will no longer be able to pay invoices by card online. Your Stripe account and any past payments are not affected, and you can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDisconnect();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disconnect.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
}
